import type { SerializedMeterRegister } from '@enmon/commons';
import type {
  SerializedBillingSetting,
  SerializedFloorPlan,
  SerializedLease,
  SerializedLeaseUnit,
  SerializedMeter,
  SerializedSupplyPoint,
  SerializedTenant,
  SerializedUser,
  Source,
} from '@enmon/commons/lib/enmon';
import { createAction } from '@reduxjs/toolkit';
import type { GetCustomerMeResult } from '@/services/api/customers/types/results';
import {
  ACTION_FAILED,
  CONFLICT_ERROR,
  CREATE_EMISSION_FACTOR_ERROR,
  CREATE_EMISSION_FACTOR_SUCCESS,
  DELETE_EMISSION_FACTOR_ERROR,
  DELETE_EMISSION_FACTOR_SUCCESS,
  EDIT_EMISSION_FACTOR_ERROR,
  EDIT_EMISSION_FACTOR_SUCCESS,
  FETCHING_DATA_FAILED,
  PREFIX,
} from './constants';

// TODO: migrate gradually to `createAction`
export function actionFailed() {
  return {
    type: ACTION_FAILED,
  };
}

export function fetchingDataFailed() {
  return {
    type: FETCHING_DATA_FAILED,
  };
}
// TODO: end of prev TODO

// TODO 2: migrate gradually to encapsulated actions, e.g.:
// export const entityActions = { create: { success: createAction(...), error: ... }, update: { ... }, ... }
export const createEmissionFactorSuccessAction = createAction(CREATE_EMISSION_FACTOR_SUCCESS);
export const createEmissionFactorErrorAction = createAction(CREATE_EMISSION_FACTOR_ERROR);

export const deleteEmissionFactorSuccessAction = createAction(DELETE_EMISSION_FACTOR_SUCCESS);
export const deleteEmissionFactorErrorAction = createAction(DELETE_EMISSION_FACTOR_ERROR);

export const editEmissionFactorSuccessAction = createAction(EDIT_EMISSION_FACTOR_SUCCESS);
export const editEmissionFactorErrorAction = createAction(EDIT_EMISSION_FACTOR_ERROR);

export const conflictErrorAction = createAction<{ code: string }>(CONFLICT_ERROR);

export const fileActions = {
  edit: {
    success: createAction(`${PREFIX}EDIT_FILE_SUCCESS`),
    error: createAction(`${PREFIX}EDIT_FILE_ERROR`),
  },
  uploadFile: {
    success: createAction(`${PREFIX}UPLOAD_FILE_SUCCESS`),
    error: createAction(`${PREFIX}UPLOAD_FILE_ERROR`),
  },
  uploadUnsupportedFile: createAction(`${PREFIX}UPLOAD_UNSUPPORTED_FILE`),
  uploadFiles: {
    success: createAction(`${PREFIX}UPLOAD_FILES_SUCCESS`),
    error: createAction(`${PREFIX}UPLOAD_FILE_ERROR`),
  },
  deleteFile: {
    success: createAction(`${PREFIX}DELETE_FILE_SUCCESS`),
    error: createAction(`${PREFIX}DELETE_FILE_ERROR`),
  },
  downloadFile: {
    error: createAction(`${PREFIX}DOWNLOAD_FILE_ERROR`),
  },
  fileLimit: {
    error: createAction(`${PREFIX}FILE_LIMIT_ERROR`),
  },
  validation: {
    error: createAction(`${PREFIX}FILE_VALIDATION_ERROR`),
  },
};
// TODO: end of prev TODO 2

// FORMATTED ACTIONS
export const authActions = {
  login: {
    success: createAction<void>(`${PREFIX}auth/login/success`),
    error: createAction<unknown>(`${PREFIX}auth/login/error`),
  },
  logout: {
    success: createAction<void>(`${PREFIX}auth/logout/success`),
    error: createAction<unknown>(`${PREFIX}auth/logout/error`),
  },
  otp: {
    success: createAction<void>(`${PREFIX}auth/otp/success`),
  },
  loginToCustomer: {
    success: createAction<GetCustomerMeResult>(`${PREFIX}auth/loginToCustomer/success`),
    error: createAction<unknown>(`${PREFIX}auth/loginToCustomer/error`),
  },
  logoutFromCustomer: {
    success: createAction<void>(`${PREFIX}auth/logoutFromCustomer/success`),
    error: createAction<unknown>(`${PREFIX}auth/logoutFromCustomer/error`),
  },
  setPassword: {
    success: createAction<void>(`${PREFIX}auth/setPassword/success`),
  },
  resetPassword: {
    success: createAction<void>(`${PREFIX}auth/resetPassword/success`),
    error: createAction<void>(`${PREFIX}auth/resetPassword/error`),
  },
  loadSession: {
    success: createAction<{ customerData: GetCustomerMeResult | null; user: SerializedUser }>(
      `${PREFIX}auth/loadSession/success`,
    ),
  },
  twoFactor: {
    verifyTotp: {
      success: createAction<void>(`${PREFIX}auth/twoFactor/verify/success`),
    },
    verifyBackupCode: {
      success: createAction<void>(`${PREFIX}auth/twoFactor/verifyBackupCode/success`),
    },
    disable: {
      success: createAction<void>(`${PREFIX}auth/twoFactor/disable/success`),
      error: createAction<void>(`${PREFIX}auth/twoFactor/disable/error`),
    },
  },
} as const;

export const dashboardActions = {
  load: {
    error: createAction(`${PREFIX}dashboard/load/error`),
  },
  suggestionAcknowledgment: {
    success: createAction(`${PREFIX}dashboard/suggestion/success`),
    error: createAction(`${PREFIX}dashboard/suggestion/error`),
  },
} as const;

export const userActions = {
  loadMultiple: {
    success: createAction(`${PREFIX}user/load/success`),
    error: createAction(`${PREFIX}user/load/error`),
  },
  create: {
    success: createAction(`${PREFIX}user/create/success`),
    error: createAction(`${PREFIX}user/create/error`),
  },
  update: {
    success: createAction(`${PREFIX}user/update/success`),
    error: createAction(`${PREFIX}user/update/error`),
  },
  updateMySettings: {
    success: createAction(`${PREFIX}user/userSettings/update/success`),
    error: createAction(`${PREFIX}user/userSettings/update/error`),
  },
  forceResetPassword: {
    success: createAction(`${PREFIX}user/password/forceReset/success`),
    error: createAction(`${PREFIX}user/password/forceReset/error`),
  },
  resetPassword: {
    success: createAction(`${PREFIX}user/password/reset/success`),
    error: createAction(`${PREFIX}user/password/reset/error`),
  },
  changePassword: {
    success: createAction(`${PREFIX}user/password/change/success`),
    error: createAction(`${PREFIX}user/password/change/error`),
  },
  delete: {
    success: createAction<void>(`${PREFIX}user/delete/success`),
    error: createAction(`${PREFIX}user/delete/error`),
  },
} as const;

export const userFavouritesActions = {
  create: {
    success: createAction(`${PREFIX}user/favourites/create/success`),
    error: createAction(`${PREFIX}user/favourites/create/error`),
  },
  update: {
    success: createAction(`${PREFIX}user/favourites/update/success`),
    error: createAction(`${PREFIX}user/favourites/update/error`),
  },
  delete: {
    success: createAction(`${PREFIX}user/favourites/delete/success`),
    error: createAction(`${PREFIX}user/favourites/delete/error`),
  },
} as const;

export const establishmentActions = {
  create: {
    success: createAction(`${PREFIX}establishment/create/success`),
    error: createAction(`${PREFIX}establishment/create/error`),
  },
  delete: {
    success: createAction(`${PREFIX}establishment/delete/success`),
    error: createAction(`${PREFIX}establishment/delete/error`),
  },
  update: {
    success: createAction(`${PREFIX}establishment/update/success`),
    error: createAction(`${PREFIX}establishment/update/error`),
  },
  import: {
    success: createAction(`${PREFIX}establishment/import/success`),
    error: createAction(`${PREFIX}establishment/import/error`),
  },
};

export const supplyPointActions = {
  create: {
    success: createAction(`${PREFIX}supplyPoint/create/success`),
    error: createAction(`${PREFIX}supplyPoint/create/error`),
  },
  validation: {
    error: createAction(`${PREFIX}supplyPoint/validation/error`),
  },
  delete: {
    success: createAction<{ count: number }>(`${PREFIX}supplyPoint/delete/success`),
    error: createAction<{ count: number }>(`${PREFIX}supplyPoint/delete/error`),
  },
  update: {
    success: createAction(`${PREFIX}supplyPoint/update/success`),
    error: createAction(`${PREFIX}supplyPoint/update/error`),
  },
  move: {
    success: createAction<{ count: number }>(`${PREFIX}supplyPoint/move/success`),
    error: createAction<{ count: number }>(`${PREFIX}supplyPoint/move/error`),
  },
  suspend: {
    success: createAction<{ count: number }>(`${PREFIX}supplyPoint/suspend/success`),
    error: createAction<{ count: number }>(`${PREFIX}supplyPoint/suspend/error`),
  },
  toggleStatus: {
    success: createAction<Pick<SerializedSupplyPoint, 'name' | 'status'>>(`${PREFIX}supplyPoint/toggleStatus/success`),
    error: createAction<Pick<SerializedSupplyPoint, 'name' | 'status'>>(`${PREFIX}supplyPoint/toggleStatus/error`),
  },
  changeSupplier: {
    success: createAction<{ count: number }>(`${PREFIX}supplyPoint/changeSupplier/success`),
    error: createAction<{ count: number }>(`${PREFIX}supplyPoint/changeSupplier/error`),
  },
  revision: {
    create: {
      success: createAction(`${PREFIX}supplyPoint/revision/create/success`),
      error: createAction(`${PREFIX}supplyPoint/revision/create/error`),
    },
    update: {
      success: createAction(`${PREFIX}supplyPoint/revision/update/success`),
      error: createAction(`${PREFIX}supplyPoint/revision/update/error`),
    },
  },
  getMetadata: {
    error: createAction(`${PREFIX}supplyPoint/getSupplyPointsMetadata/error`),
  },
};

export const recordsActions = {
  create: {
    success: createAction<Source>(`${PREFIX}record/create/success`),
    error: createAction<Source>(`${PREFIX}record/create/error`),
  },
  delete: {
    success: createAction<Source>(`${PREFIX}record/delete/success`),
    error: createAction<Source>(`${PREFIX}record/delete/error`),
  },
  update: {
    success: createAction<Source>(`${PREFIX}record/update/success`),
    error: createAction<Source>(`${PREFIX}record/update/error`),
  },
};

export const tenantActions = {
  create: {
    success: createAction<SerializedTenant>(`${PREFIX}tenant/create/success`),
    error: createAction<unknown>(`${PREFIX}tenant/create/error`),
  },
  update: {
    success: createAction<SerializedTenant>(`${PREFIX}tenant/update/success`),
    error: createAction<unknown>(`${PREFIX}tenant/update/error`),
  },
  delete: {
    success: createAction<void>(`${PREFIX}tenant/delete/success`),
    error: createAction<unknown>(`${PREFIX}tenant/delete/error`),
  },
} as const;

export const leaseUnitActions = {
  create: {
    success: createAction<SerializedLeaseUnit>(`${PREFIX}leaseUnit/create/success`),
    error: createAction<unknown>(`${PREFIX}leaseUnit/create/error`),
  },
  update: {
    success: createAction<SerializedLeaseUnit>(`${PREFIX}leaseUnit/update/success`),
    error: createAction<unknown>(`${PREFIX}leaseUnit/update/error`),
  },
  delete: {
    success: createAction<void>(`${PREFIX}leaseUnit/delete/success`),
    error: createAction<unknown>(`${PREFIX}leaseUnit/delete/error`),
  },
} as const;

export const billingActions = {
  delete: {
    success: createAction<void>(`${PREFIX}billing/delete/success`),
    error: createAction<unknown>(`${PREFIX}billing/delete/error`),
  },
  calculate: {
    success: createAction<void>(`${PREFIX}billing/calculate/success`),
    error: createAction<unknown>(`${PREFIX}billing/calculate/error`),
  },
  preview: {
    error: createAction<unknown>(`${PREFIX}billing/preview/error`),
  },
};

export const billingSettingsActions = {
  create: {
    success: createAction<SerializedBillingSetting>(`${PREFIX}billingSetting/create/success`),
    error: createAction<unknown>(`${PREFIX}billingSetting/create/error`),
  },
  update: {
    success: createAction<SerializedBillingSetting>(`${PREFIX}billingSetting/update/success`),
    error: createAction<unknown>(`${PREFIX}billingSetting/update/error`),
  },
  delete: {
    success: createAction<void>(`${PREFIX}billingSetting/delete/success`),
    error: createAction<unknown>(`${PREFIX}billingSetting/delete/error`),
  },
} as const;

export const leasesActions = {
  create: {
    success: createAction<Array<SerializedLease | null>>(`${PREFIX}leases/create/success`),
    error: createAction<unknown>(`${PREFIX}leases/create/error`),
  },
};

export const leaseActions = {
  create: {
    success: createAction<SerializedLease | null>(`${PREFIX}lease/create/success`),
    error: createAction<unknown>(`${PREFIX}lease/create/error`),
  },
  update: {
    success: createAction<SerializedLease | null>(`${PREFIX}lease/update/success`),
    error: createAction<unknown>(`${PREFIX}lease/update/error`),
  },
};

export const metersActions = {
  create: {
    success: createAction<Pick<SerializedMeter, 'name'>>(`${PREFIX}meter/create/success`),
    error: createAction<unknown>(`${PREFIX}meter/create/error`),
  },
  update: {
    success: createAction<Pick<SerializedMeter, 'name'>>(`${PREFIX}meter/update/success`),
    error: createAction<unknown>(`${PREFIX}meter/update/error`),
  },
  delete: {
    success: createAction<void>(`${PREFIX}meter/delete/success`),
    error: createAction<unknown>(`${PREFIX}meter/delete/error`),
  },
  move: {
    success: createAction<Pick<SerializedMeter, '_id' | 'name'>>(`${PREFIX}meter/move/success`),
    error: createAction<Pick<SerializedMeter, '_id' | 'name'>>(`${PREFIX}meter/move/error`),
  },
  replace: {
    success: createAction<void>(`${PREFIX}meter/replace/success`),
    error: createAction<unknown>(`${PREFIX}meter/replace/error`),
  },
  report: {
    success: createAction<void>(`${PREFIX}meter/report/success`),
    error: createAction<unknown>(`${PREFIX}meter/report/error`),
  },
  registers: {
    create: {
      success: createAction<Pick<SerializedMeterRegister, 'name'>>(`${PREFIX}meterRegister/create/success`),
      error: createAction<unknown>(`${PREFIX}meterRegister/create/error`),
    },
    update: {
      success: createAction<Pick<SerializedMeterRegister, 'name'>>(`${PREFIX}meterRegister/update/success`),
      error: createAction<unknown>(`${PREFIX}meterRegister/update/error`),
    },
    delete: {
      success: createAction<void>(`${PREFIX}meterRegister/delete/success`),
      error: createAction<unknown>(`${PREFIX}meterRegister/delete/error`),
    },
  },
  revisions: {
    update: {
      success: createAction<void>(`${PREFIX}meterRevision/update/success`),
      error: createAction<unknown>(`${PREFIX}meterRevision/update/error`),
    },
    delete: {
      success: createAction<void>(`${PREFIX}meterRevision/delete/success`),
      error: createAction<unknown>(`${PREFIX}meterRevision/delete/error`),
    },
    revert: {
      success: createAction<void>(`${PREFIX}meterRevision/revert/success`),
      error: createAction<unknown>(`${PREFIX}meterRevision/revert/error`),
    },
  },
};

export const readingActions = {
  remote: {
    value: {
      delete: {
        success: createAction(`${PREFIX}reading/remote/value/delete/success` as const),
      },
    },
  },
};

export const floorPlanActions = {
  create: {
    success: createAction<SerializedFloorPlan | null>(`${PREFIX}floorPlan/create/success`),
    error: createAction<unknown>(`${PREFIX}floorPlan/create/error`),
  },
  update: {
    success: createAction<SerializedFloorPlan | null>(`${PREFIX}floorPlan/update/success`),
    error: createAction<unknown>(`${PREFIX}floorPlan/update/error`),
  },
  delete: {
    success: createAction<void>(`${PREFIX}floorPlan/delete/success`),
    error: createAction<unknown>(`${PREFIX}floorPlan/delete/error`),
  },
};

export const bubbleTourActions = {
  changeStep: {
    error: createAction<unknown>(`${PREFIX}bubbleTour/create/error`),
  },
};

export const bugReportActions = {
  create: {
    success: createAction<void>(`${PREFIX}bugReport/create/success`),
  },
} as const;

export const askNewMeterModelActions = {
  create: {
    success: createAction<void>(`${PREFIX}askNewMeterModel/create/success`),
  },
} as const;
